import { InfoLineIcon } from '@hedepy/icons/refactor'
import * as Sentry from '@sentry/nextjs'
import type { ReactNode } from 'react'
import { useSWRConfig } from 'swr'

import { getTranslate } from '@/legacy-src/utilities/i18n'

import { Alert } from '@/src/domains/design-system/components/Alert'
import { Button } from '@/src/domains/design-system/components/Button'
import { getSwrErrorKey } from '@/src/domains/swr/utils/swrErrorKey'

export interface ErrorBoundaryProps {
  getErrorMessage?: (error: unknown) => string
  noFallback?: boolean
  children: ReactNode
}

export const ErrorBoundary = ({ getErrorMessage, noFallback = false, children }: ErrorBoundaryProps) => {
  const { mutate } = useSWRConfig()

  const { t } = getTranslate()

  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => {
        if (noFallback) {
          return null
        }

        const key = getSwrErrorKey(error)

        const handleRetry = () => {
          if (key) {
            void mutate(key, undefined, { revalidate: false })
          }

          resetError()
        }

        return (
          <Alert
            variant="yellow"
            icon={<InfoLineIcon />}
            title={getErrorMessage?.(error) ?? t('errors.defaultError')}
            action={
              <Button onClick={handleRetry} size="small">
                {t('component.button.retry')}
              </Button>
            }
          />
        )
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  )
}
